import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Modal } from "react-bootstrap";
import { AiFillHeart, AiOutlineHeart } from "react-icons/ai";
import { MdRemoveCircle } from "react-icons/md";
import { IoIosAddCircle } from "react-icons/io";
import { useDispatch, useSelector } from "react-redux";
import { addProductToCart, updateCartItemQuantity, removeProductFromCart } from "../redux/cartSlice";
import { addProductToWishlist, removeProductFromWishlist } from "../redux/WishlistSlice";
import { getFallbackImage, loadImageWithCache } from "../utils/imageLoader";
import "./ProductModal.css";
import { toast } from 'react-hot-toast';

// Styles
const styles = {
  quantityContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    textAlign: "center",
    justifyContent: "center",
    marginBottom: "10px",
  },
  quantityButton: {
    border: "none",
    backgroundColor: "transparent",
    color: "#82278A",
    fontSize: "30px",
    fontWeight: "800",
    width: "34px",
    height: "24px",
    borderRadius: "20%",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "4px",
    marginBottom: "15px",
  },
  quantity: {
    fontSize: "20px",
    fontWeight: "600",
    color: "#82278A",
    minWidth: "20px",
    textAlign: "center",
    marginBottom: "15px",
  },
  colorSizeButton: {
    padding: "8px 16px",
    border: "2px solid #82278A",
    borderRadius: "20px",
    backgroundColor: "white",
    color: "#82278A",
    cursor: "pointer",
    margin: "4px",
    fontSize: "14px",
    fontWeight: "500",
    transition: "all 0.2s ease",
    outline: "none",
  },
  colorSizeButtonDisabled: {
    padding: "8px 16px",
    border: "2px solid #ccc",
    borderRadius: "20px",
    backgroundColor: "#f5f5f5",
    color: "#999",
    cursor: "not-allowed",
    margin: "4px",
    fontSize: "14px",
    fontWeight: "500",
    opacity: 0.5,
    outline: "none",
  },
  colorSizeButtonSelected: {
    padding: "8px 16px",
    border: "2px solid #82278A",
    borderRadius: "20px",
    backgroundColor: "#82278A",
    color: "white",
    cursor: "pointer",
    margin: "4px",
    fontSize: "14px",
    fontWeight: "500",
    transition: "all 0.2s ease",
    outline: "none",
  },
  selectionContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    marginTop: "8px",
    marginBottom: "16px",
  },
  selectedBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    backgroundColor: "#f0f0f0",
    padding: "4px 8px",
    borderRadius: "12px",
    fontSize: "12px",
    margin: "2px",
  },
};
// Product Modal Component
const ProductModal = ({ post, isOpen, onClose, initialSelectedColor = '', initialSelectedSize = '' }) => {
  // Redux hooks
  const dispatch = useDispatch();
  const wishlistItems = useSelector(state => state.wishlist.items);
  const cartItems = useSelector(state => state.cart.items);

  // Local state
  const [selectedColors, setSelectedColors] = useState(initialSelectedColor ? [initialSelectedColor] : []);
  const [selectedSizes, setSelectedSizes] = useState(initialSelectedSize ? [initialSelectedSize] : []);
  const [loadedImages, setLoadedImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);

  // Refs
  const imageContainerRef = useRef(null);
  const mainImageRef = useRef(null);
  const lastThumbnailClickRef = useRef(0);
  const [mainImageSrc, setMainImageSrc] = useState('');

  // Derived states
  const isInWishlist = post ? wishlistItems.some(item => item.id === post._id) : false;
  
  // multiple selections, quantity is based on cart items matching this product
  const currentItem = post ? cartItems.find(item => item.id === post._id) : null;
  const quantity = currentItem ? currentItem.quantity : 0;

  // Extract image URLs from API response
  const extractImageUrls = (imagesArray) => {
    if (!imagesArray || !Array.isArray(imagesArray)) return [];
    
    return imagesArray.map(img => {
      // Handle both string URLs and object format from API
      if (typeof img === 'string') return img;
      if (img && img.url) return img.url;
      if (img && img.public_id) {
        // Construct URL from Cloudinary public_id
        return `https://res.cloudinary.com/dytcxexpb/image/upload/${img.public_id}`;
      }
      return getFallbackImage();
    }).filter(url => url && url !== getFallbackImage());
  };

  // Process images from API response
  const images = useMemo(() => extractImageUrls(post?.images), [post?.images]);
  
  // Process colors from API response
  const uniqueColors = useMemo(() => {
    if (!post) return [];
    
    // Colors field exists
    if (post?.colors) {
      if (Array.isArray(post.colors)) {
        // Extract colors
        return post.colors.flatMap(c => {
          // Handle different formats
          if (typeof c === 'string') {
            return c.split(',').map(color => color.trim());
          }
          if (c && typeof c === 'object' && c.color) {
            return [c.color].filter(Boolean);
          }
          return [c].filter(Boolean);
        }).filter(Boolean);
      }
      // Single string of colors
      if (typeof post.colors === 'string') {
        return post.colors.split(',').map(c => c.trim()).filter(Boolean);
      }
    }
    
    // Variants field
    if (post?.variants) {
      const colors = new Set();
      post.variants.forEach(variant => {
        // Extract colors from each variant
        if (variant.color) {
          if (Array.isArray(variant.color)) {
            // Multiple colors in variant
            variant.color.forEach(color => colors.add(color.trim()));
          } else if (typeof variant.color === 'string') {
            colors.add(variant.color.trim());
          }
        }
      });
      return Array.from(colors).filter(Boolean);
    }
    
    return [];
  }, [post]);

  // Process sizes from API response
  const uniqueSizes = useMemo(() => {
    if (!post) return [];
    
    // Sizes fiel
    if (post?.sizes) {
      if (Array.isArray(post.sizes)) {
        // Extract sizes
        return post.sizes.flatMap(s => {
          // Handle different formats
          if (typeof s === 'string') {
            return s.split(',').map(size => size.trim());
          }
          if (s && typeof s === 'object' && s.size) {
            return [s.size].filter(Boolean);
          }
          return [s].filter(Boolean);
        }).filter(Boolean);
      }
      // Single string of sizes
      if (typeof post.sizes === 'string') {
        return post.sizes.split(',').map(s => s.trim()).filter(Boolean);
      }
    }
    
    // Variants field exist
    if (post?.variants) {
      // Extract sizes from variants
      const sizes = new Set();
      post.variants.forEach(variant => {
        if (variant.size) {
          // Handle multiple sizes in variant
          if (Array.isArray(variant.size)) {
            variant.size.forEach(size => sizes.add(size.trim()));
          } else if (typeof variant.size === 'string') {
            sizes.add(variant.size.trim());
          }
        }
      });
      return Array.from(sizes).filter(Boolean);
    }
    
    return [];
  }, [post]);

  // Get available colors based on selected sizes 
  const availableColors = useMemo(() => {
    if (!post?.variants || selectedSizes.length === 0) return uniqueColors;
    
    // Find colors that are available for ALL selected sizes
    const colorsPerSize = selectedSizes.map(selectedSize => {
      const colors = new Set();
      // Check each variant for the selected size
      post.variants.forEach(variant => {
        const variantSizes = Array.isArray(variant.size) ? variant.size : [variant.size].filter(Boolean);
        // Check if variant includes the selected size
        if (variantSizes.includes(selectedSize)) {
          const variantColors = Array.isArray(variant.color) ? variant.color : [variant.color].filter(Boolean);
          variantColors.forEach(color => colors.add(color));
        }
      });
      return colors;
    });
    
    // Get intersection - colors that appear in ALL size selections
    if (colorsPerSize.length === 0) return uniqueColors;
    let intersection = new Set(colorsPerSize[0]);
    // Iterate to find common colors
    for (let i = 1; i < colorsPerSize.length; i++) {
      intersection = new Set([...intersection].filter(color => colorsPerSize[i].has(color)));
    }
    return Array.from(intersection);
  }, [post, selectedSizes, uniqueColors]);

  // Get available sizes based on selected colors
  const availableSizes = useMemo(() => {
    if (!post?.variants || selectedColors.length === 0) return uniqueSizes;
    
    // Find sizes for all selected colors
    const sizesPerColor = selectedColors.map(selectedColor => {
      // Check each variant for the selected color
      const sizes = new Set();
      post.variants.forEach(variant => {
        // Check if variant includes the selected color
        const variantColors = Array.isArray(variant.color) ? variant.color : [variant.color].filter(Boolean);
        if (variantColors.includes(selectedColor)) {
          // Extract sizes
          const variantSizes = Array.isArray(variant.size) ? variant.size : [variant.size].filter(Boolean);
          // Handle multiple sizes in variant
          variantSizes.forEach(size => sizes.add(size));
        }
      });
      return sizes;
    });
    
    // Get intersection - sizes that appear in all color selections
    if (sizesPerColor.length === 0) return uniqueSizes;
    // Calculate intersection of sizes
    let intersection = new Set(sizesPerColor[0]);
    // Iterate to find common sizes
    for (let i = 1; i < sizesPerColor.length; i++) {
      intersection = new Set([...intersection].filter(size => sizesPerColor[i].has(size)));
    }
    
    return Array.from(intersection);
  }, [post, selectedColors, uniqueSizes]);

  // Get all matching variants for selected combinations
  const selectedVariants = useMemo(() => {
    if (!post?.variants) return [];
    
    if (selectedColors.length === 0 || selectedSizes.length === 0) {
      return [post.variants[0]];
    }
    
    const variants = [];
    // Find variants matching selected colors and sizes
    selectedColors.forEach(color => {
      selectedSizes.forEach(size => {
        // Find variant matching this color/size combination
        const variant = post.variants.find(v => {
          const variantColors = Array.isArray(v.color) ? v.color : [v.color].filter(Boolean);
          const variantSizes = Array.isArray(v.size) ? v.size : [v.size].filter(Boolean);
          return variantColors.includes(color) && variantSizes.includes(size);
        });
        if (variant) variants.push(variant);
      });
    });
    
    return variants.length > 0 ? variants : [post.variants[0]];
  }, [post, selectedColors, selectedSizes]);

  // Calculate price range from selected variants
  const priceDisplay = useMemo(() => {
    if (selectedVariants.length === 0) return '0.00';
    if (selectedVariants.length === 1) return selectedVariants[0].price.toFixed(2);
    // Multiple variants selected, show range
    const prices = selectedVariants.map(v => v.price);
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    // Show price range if different
    if (minPrice === maxPrice) return minPrice.toFixed(2);
    return `${minPrice.toFixed(2)} - ${maxPrice.toFixed(2)}`;
  }, [selectedVariants]);

  const price = selectedVariants[0]?.price || 0;

  useEffect(() => {
    if (isOpen && post) {
      // Check if product variants are already in cart
      const cartVariants = cartItems.filter(item => item.id === post._id);
      
      if (cartVariants.length > 0) {
        // Restore selections from cart items
        const colorsInCart = [...new Set(cartVariants.map(item => item.selectedColor).filter(Boolean))];
        const sizesInCart = [...new Set(cartVariants.map(item => item.selectedSize).filter(Boolean))];
        // Set selections from cart
        if (colorsInCart.length > 0) setSelectedColors(colorsInCart);
        if (sizesInCart.length > 0) setSelectedSizes(sizesInCart);
      } else if (initialSelectedColor || initialSelectedSize) {
        // Use initial values if provided
        if (initialSelectedColor) setSelectedColors([initialSelectedColor]);
        if (initialSelectedSize) setSelectedSizes([initialSelectedSize]);
      } else if (post?.variants?.length) {
        // Default to first variant
        const first = post.variants[0];
        // Handle multiple colors in variant
        const firstColor = Array.isArray(first.color) ? first.color[0] : first.color;
        const firstSize = Array.isArray(first.size) ? first.size[0] : first.size;
        if (firstColor) setSelectedColors([firstColor]);
        if (firstSize) setSelectedSizes([firstSize]);
      }
    }
  }, [isOpen, post, initialSelectedColor, initialSelectedSize, cartItems]);

  useEffect(() => {
    // Load and cache images when post or images change
    const loadImages = async () => {
      if (images.length > 0) {
        const loaded = await Promise.all(images.map(img => loadImageWithCache(img)));
        setLoadedImages(loaded);
      } else {
        setLoadedImages([]);
      }
      setCurrentImageIndex(0);
    };
    loadImages();
  }, [post, images]);

  // Keep a separate main image src for immediate rendering on thumbnail click
  useEffect(() => {
    // Update main image src when loadedImages or currentImageIndex changes
    if (loadedImages && loadedImages.length > 0) {
      const src = loadedImages[currentImageIndex] || loadedImages[0];
      setMainImageSrc(src);
    } else if (images && images.length > 0) {
      setMainImageSrc(images[0]);
    } else {
      setMainImageSrc('');
    }
  }, [loadedImages, currentImageIndex, images]);

  useEffect(() => {
    // Scroll to current image in carousel when index changes
    if (imageContainerRef.current && loadedImages.length > 0) {
      // Calculate scroll position based on current image index
      const containerWidth = imageContainerRef.current.clientWidth;
      imageContainerRef.current.scrollTo({
        left: currentImageIndex * containerWidth,
        behavior: 'smooth'
      });
    }
  }, [currentImageIndex, loadedImages]);

  // Touch Handlers
  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStartX(touch.clientX);
    // condition for mobile touch
    if (imageContainerRef.current) {
      imageContainerRef.current.style.scrollSnapType = 'none';
      imageContainerRef.current.style.scrollBehavior = 'auto';
    }
  };
  // Move handler
  const handleTouchMove = (e) => {
    if (!isDragging || !imageContainerRef.current) return;
    // touch move
    const touch = e.touches[0];
    const currentX = touch.clientX;
    const diffX = currentX - dragStartX;

    // Adjust scroll position
    imageContainerRef.current.scrollLeft -= diffX * 0.8;
    setDragStartX(currentX);
  };

  // End touch
  const handleTouchEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    if (imageContainerRef.current) {
      imageContainerRef.current.style.scrollSnapType = 'x mandatory';
      imageContainerRef.current.style.scrollBehavior = 'smooth';

      // Calculate nearest image index
      const containerWidth = imageContainerRef.current.clientWidth;
      const scrollLeft = imageContainerRef.current.scrollLeft;
      const newIndex = Math.round(scrollLeft / containerWidth);

      // Clamp index within bounds
      setCurrentImageIndex(Math.max(0, Math.min(newIndex, images.length - 1)));
      imageContainerRef.current.scrollTo({ 
        left: newIndex * containerWidth, 
        behavior: 'smooth' 
      });
    }
  };

  // Mouse Handlers
  const handleMouseDown = (e) => {
    if (e.touches) return;
    setIsDragging(true);
    setDragStartX(e.clientX);
    if (imageContainerRef.current) imageContainerRef.current.style.scrollSnapType = 'none';
    e.preventDefault();
  };

  // Move handler
  const handleMouseMove = (e) => {
    // mouse move
    if (!isDragging || !imageContainerRef.current || e.touches) return;
    const currentX = e.clientX;
    const offset = currentX - dragStartX;
    imageContainerRef.current.scrollLeft -= offset;
    setDragStartX(currentX);
  };
  
  // End mouse
  const handleMouseUp = () => {
    if (!isDragging) return;
    setIsDragging(false);
    // Calculate nearest image index
    if (imageContainerRef.current) {
      imageContainerRef.current.style.scrollSnapType = 'x mandatory';
      // Clamp index within bounds
      const containerWidth = imageContainerRef.current.clientWidth;
      const scrollLeft = imageContainerRef.current.scrollLeft;
      const newIndex = Math.round(scrollLeft / containerWidth);
      setCurrentImageIndex(Math.max(0, Math.min(newIndex, images.length - 1)));
      imageContainerRef.current.scrollTo({ left: Math.round(newIndex * containerWidth), behavior: 'smooth' });
    }
  };
  // Scroll handler
  const handleScroll = () => {
    if (imageContainerRef.current) {
      // Calculate current image index based on scroll position
      const containerWidth = imageContainerRef.current.clientWidth;
      const scrollLeft = imageContainerRef.current.scrollLeft;
      const newIndex = Math.round(scrollLeft / containerWidth);
      if (newIndex !== currentImageIndex && newIndex >= 0 && newIndex < images.length) {
        setCurrentImageIndex(newIndex);
      }
    }
  };

  // Thumbnail click handler
  const handleThumbnailClick = useCallback((index) => {
    const now = Date.now();
    // Debounce rapid clicks
    if (now - (lastThumbnailClickRef.current || 0) < 200) return;
    lastThumbnailClickRef.current = now;

    setCurrentImageIndex(index);
    // update main image src immediately for reliable UI change
    if (loadedImages && loadedImages[index]) {
      setMainImageSrc(loadedImages[index]);
    } else if (images && images[index]) {
      setMainImageSrc(images[index]);
    }
    // Keep scroll adjustment
    try {
      // Scroll to the selected thumbnail
      if (imageContainerRef.current) {
        const containerWidth = imageContainerRef.current.clientWidth || 1;
        // Scroll to the selected image
        imageContainerRef.current.scrollTo({ 
          left: Math.round(index * containerWidth), 
          behavior: 'smooth' 
        });
      }
    } catch (e) {
    }
  }, [images, loadedImages]);

  // Color selection handler - multi-selection with dynamic filtering
  const handleColorSelect = (color) => {
    setSelectedColors(prev => {
      if (prev.includes(color)) {
        // Remove color if already selected
        return prev.filter(c => c !== color);
      } else {
        // Add color if not selected
        return [...prev, color];
      }
    });
  };

  // Size selection handler - multi-selection with dynamic filtering
  const handleSizeSelect = (size) => {
    setSelectedSizes(prev => {
      if (prev.includes(size)) {
        // Remove size if already selected
        return prev.filter(s => s !== size);
      } else {
        // Add size if not selected
        return [...prev, size];
      }
    });
  };

  // Add to cart handler - adds all selected color/size combinations
  const handleAddToCart = () => {
    if (!post?.variants?.length) return;

    // Ensure at least one color and size selected
    if (selectedColors.length === 0 || selectedSizes.length === 0) {
      toast.error("Please select at least one color and one size.");
      return;
    }

    // Add all selected color/size combinations to cart
    const addedItems = [];
    const failedItems = [];

    // Iterate through all selected combinations
    selectedColors.forEach(color => {
      selectedSizes.forEach(size => {
        // Find matching variant
        const matchingVariant = post.variants.find(v => {

          // Check if variant matches selected color and size
          const variantColors = Array.isArray(v.color) ? v.color : [v.color].filter(Boolean);
          const variantSizes = Array.isArray(v.size) ? v.size : [v.size].filter(Boolean);
          return variantColors.includes(color) && variantSizes.includes(size);
        });

        // Add to cart if variant found and in stock
        if (matchingVariant && matchingVariant.stock > 0) {
          // Construct cart item
          const itemToAdd = {
            ...post,
            id: post._id,
            price: matchingVariant.price,
            image: images[0] || getFallbackImage(),
            title: post.name,
            variantId: matchingVariant._id,
            selectedColor: color,
            selectedSize: size
          };
          // Add without showing individual notifications
          dispatch(addProductToCart(itemToAdd, 1, { showNotification: false }));

          // Track added items for notification
          addedItems.push(`${color}/${size} (₹${matchingVariant.price})`);
        } else if (matchingVariant && matchingVariant.stock === 0) {
          failedItems.push(`${color}/${size} (Out of stock)`);
        } else {
          failedItems.push(`${color}/${size} (Not available)`);
        }
      });
    });

    // Show single combined notification for all added items
    if (addedItems.length > 0) {
      // Custom event for cart addition notification
      window.dispatchEvent(new CustomEvent('cartNotification', { 
        detail: { 
          type: 'cart', 
          subType: 'add', 
          message: addedItems.length === 1 ? 'Added to cart' : `Added ${addedItems.length} items to cart`,
          item: post,
          variantCount: addedItems.length
        }
      }));
    }
    if (failedItems.length > 0) {
      toast.error(`Could not add:\n${failedItems.join(', ')}`, { duration: 4000 });
    }
  };

  // Increment quantity handler
  const handleIncrement = () => {
    if (currentItem) {
      dispatch(updateCartItemQuantity(currentItem, quantity + 1));
    }
  };
  // Decrement quantity handler
  const handleDecrement = () => {
    // Remove item if quantity reaches zero
    if (currentItem) {
      if (quantity === 1) {
        dispatch(removeProductFromCart(currentItem));
      } else {
        dispatch(updateCartItemQuantity(currentItem, quantity - 1));
      }
    }
  };
  // Wishlist toggle handler
  const toggleWishlist = () => {
    const safeImage = images[0] || getFallbackImage();
    const wishlistItemData = {
      ...post,
      id: post._id,
      price,
      image: safeImage,
      title: post.name,
      wishlistId: wishlistItems.find(item => item.id === post._id)?.wishlistId
    };
    
    // Add or remove from wishlist
    if (isInWishlist) {
      dispatch(removeProductFromWishlist(wishlistItemData));
    } else {
      dispatch(addProductToWishlist(wishlistItemData));
    }
  };

  if (!post) return null;

  const imageSize = '100%';

  return (
    <Modal show={isOpen} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton style={{ border: 'none' }}>
        <Modal.Title>{post.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ border: 'none' }}>
        <div className="modal-main-content">
          
          <div className="image-section-wrapper">
              {/* Main Image Carousel */}
              <div
                ref={imageContainerRef}
                className="image-container"
                style={{ position: 'relative' }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onScroll={handleScroll}
              >
                <div className="carousel-images">
                  {mainImageSrc ? (
                    <div 
                      style={{ 
                        position: 'relative', 
                        display: 'inline-block',
                        width: imageSize,
                        height: imageSize
                      }}
                    >
                      <img
                        ref={mainImageRef}
                        key={mainImageSrc}
                        src={mainImageSrc}
                        alt={`${post.name} main`}
                        className="modal-image"
                        style={{
                          flexShrink: 0,
                          width: imageSize,
                          height: imageSize,
                          objectFit: 'contain',
                          borderRadius: '8px',
                          position: 'static',
                          transform: 'none'
                        }}
                        onError={(e) => {
                          console.error("Main image failed to load:", mainImageSrc);
                          e.target.src = getFallbackImage();
                        }}
                      />
                    </div>
                  ) : (
                    <img
                      src={getFallbackImage()}
                      alt={`${post.name} fallback`}
                      className="modal-image"
                      style={{ width: imageSize, height: imageSize }}
                    />
                  )}
                </div>
              </div>

              {/* Thumbnails Gallery*/}
              {images.length > 1 && (
                <div className="thumbnails-container">
                  {images.map((img, index) => (
                    <div 
                      key={img || index}
                      className={`thumbnail-item ${index === currentImageIndex ? 'active' : ''}`}
                      onClick={() => handleThumbnailClick(index)}
                    >
                      <img
                        src={img}
                        alt={`${post.name} thumbnail ${index + 1}`}
                        className="thumbnail-image"
                        onError={(e) => { 
                          console.error("Thumbnail failed to load:", img);
                          e.target.src = getFallbackImage(); 
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}
          </div>

          <div className="details-section">
            <h5>Description</h5>
            <p>{post.description || "No description available."}</p>
            <div style={{ marginBottom: '16px' }}>
              <h5 style={{ marginBottom: '8px' }}>Price: ₹{priceDisplay}</h5>
              {selectedColors.length > 0 && selectedSizes.length > 0 && (
                <div style={{ fontSize: '13px', color: '#666' }}>
                  <div style={{ marginBottom: '4px' }}>
                    <strong>{selectedVariants.length} variant(s) selected</strong>
                  </div>
                  {selectedVariants.slice(0, 3).map((variant, idx) => {
                    const color = selectedColors.find(c => {
                      const vc = Array.isArray(variant.color) ? variant.color : [variant.color];
                      return vc.includes(c);
                    });
                    const size = selectedSizes.find(s => {
                      const vs = Array.isArray(variant.size) ? variant.size : [variant.size];
                      return vs.includes(s);
                    });
                    return (
                      <div key={idx} style={{ fontSize: '12px', marginBottom: '2px' }}>
                        {color}/{size}: ₹{variant.price} - 
                        {variant.stock > 0 ? (
                          <span style={{ color: '#28a745' }}> ✓ {variant.stock} in stock</span>
                        ) : (
                          <span style={{ color: '#dc3545' }}> ✗ Out of stock</span>
                        )}
                      </div>
                    );
                  })}
                  {selectedVariants.length > 3 && (
                    <div style={{ fontSize: '12px', color: '#999' }}>...and {selectedVariants.length - 3} more</div>
                  )}
                </div>
              )}
            </div>

            {uniqueColors.length > 0 && (
              <div>
                <h5>
                  Color {selectedColors.length > 0 && <span style={{ fontSize: '14px', fontWeight: '400', color: '#82287A' }}>(Selected: {selectedColors.length})</span>}
                </h5>
                {selectedSizes.length > 0 && availableColors.length < uniqueColors.length && (
                  <p style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                    Showing colors available for selected size(s)
                  </p>
                )}
                <div style={styles.selectionContainer}>
                  {(selectedSizes.length > 0 ? availableColors : uniqueColors).map(color => {
                    const isDisabled = selectedSizes.length > 0 && !availableColors.includes(color);
                    return (
                      <button
                        key={color}
                        type="button"
                        style={
                          isDisabled
                            ? styles.colorSizeButtonDisabled
                            : selectedColors.includes(color)
                              ? styles.colorSizeButtonSelected
                              : styles.colorSizeButton
                        }
                        onClick={() => !isDisabled && handleColorSelect(color)}
                        disabled={isDisabled}
                      >
                        {color}
                      </button>
                    );
                  })}
                </div>
                {selectedColors.length > 0 && (
                  <div style={{ marginTop: '8px', fontSize: '13px' }}>
                    <strong>Selected:</strong> {selectedColors.join(', ')}
                  </div>
                )}
              </div>
            )}

            {uniqueSizes.length > 0 && (
              <div>
                <h5>
                  Size {selectedSizes.length > 0 && <span style={{ fontSize: '14px', fontWeight: '400', color: '#82287A' }}>(Selected: {selectedSizes.length})</span>}
                </h5>
                {selectedColors.length > 0 && availableSizes.length < uniqueSizes.length && (
                  <p style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                    Showing sizes available for selected color(s)
                  </p>
                )}
                <div style={styles.selectionContainer}>
                  {(selectedColors.length > 0 ? availableSizes : uniqueSizes).map(size => {
                    const isDisabled = selectedColors.length > 0 && !availableSizes.includes(size);
                    return (
                      <button
                        key={size}
                        type="button"
                        style={
                          isDisabled
                            ? styles.colorSizeButtonDisabled
                            : selectedSizes.includes(size)
                              ? styles.colorSizeButtonSelected
                              : styles.colorSizeButton
                        }
                        onClick={() => !isDisabled && handleSizeSelect(size)}
                        disabled={isDisabled}
                      >
                        {size}
                      </button>
                    );
                  })}
                </div>
                {selectedSizes.length > 0 && (
                  <div style={{ marginTop: '8px', fontSize: '13px' }}>
                    <strong>Selected:</strong> {selectedSizes.join(', ')}
                  </div>
                )}
              </div>
            )}

            <div className="action-button-row">
              {quantity === 0 ? (
                <button 
                  className="btn btn-primary addToCartButton action-button" 
                  onClick={handleAddToCart} 
                  style={{ backgroundColor: "#82278A" }}
                  disabled={selectedColors.length === 0 || selectedSizes.length === 0}
                >
                  {selectedColors.length === 0 || selectedSizes.length === 0 ? 
                    "Select Color & Size" : 
                    `Add ${selectedColors.length * selectedSizes.length} to Cart`}
                </button>
              ) : (
                <div style={styles.quantityContainer}>
                  <button style={styles.quantityButton} onClick={handleDecrement} aria-label="Decrease quantity">
                    <MdRemoveCircle />
                  </button>
                  <span style={styles.quantity}>{quantity}</span>
                  <button 
                    style={styles.quantityButton} 
                    onClick={handleIncrement} 
                    aria-label="Increase quantity"
                  >
                    <IoIosAddCircle />
                  </button>
                </div>
              )}
              <button
                className={`btn wishlistButton action-button ${isInWishlist ? "btn-danger" : "btn-outline-danger"}`}
                onClick={toggleWishlist}
              >
                {isInWishlist ? <AiFillHeart /> : <AiOutlineHeart />} Wishlist
              </button>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ProductModal;